/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { baseComponentManager } from '../../../../registry/Components';
import { defaultValue } from './AreaToolbarZoom';

const AreaToolbarZoomManager: ComponentManagerFn<'AreaToolbarZoom'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'AreaToolbarZoom'>(platform, 'AreaToolbarZoom'),
    type: 'AreaToolbarZoom',
    name: 'Zoom',
    defaultValue,
    props: {},
    category: 'IoT',
    icon: { iconName: 'search-plus' },
    description: `**Overview**<br/>
The Area Toolbar Zoom component is a navigation control set for the **IoT Image Mapper**. It adds "Zoom In" (+), "Zoom Out" (-), and "Reset Zoom" buttons to the toolbar, allowing users to magnify or shrink their view of the map canvas.
Use this component to enable detailed inspection of large, high-resolution floor plans or schematics where the entire image cannot be viewed clearly at once.

**1. Functionality**<br/>
* **Zoom In (+):** Magnifies the canvas, allowing users to see smaller zones, labels, and fine details more clearly.
* **Zoom Out (-):** Shrinks the view, allowing users to see more of the overall map context and spatial relationships.
* **Pan Integration:** Zooming typically enables panning (dragging the canvas) when the image dimensions exceed the visible viewport size, allowing for seamless navigation across large assets.

**2. Configuration**<br/>
* **Zero Configuration:** Like the Undo/Redo tool, this component is a pre-configured utility with no properties to set up manually. 
* **Automatic Integration:** Adding it as a child of the **Area Toolbar** automatically wires it to the parent map's viewport logic, providing smooth scaling of both the background image and the overlaid polygons.

**3. Best Use Cases**<br/>
* **Large Floor Plans:** Navigating a map of a massive warehouse, hospital, or stadium where seeing individual seats, shelves, or rooms requires high magnification.
* **Detailed Schematics:** Inspecting intricate machinery or electrical diagrams where distinct "Safety Zones" or sensor points are small and clustered together.
* **Mobile & Tablet Users:** Helping users on smaller screens or touch devices navigate a large, complex image comfortably by focusing on specific quadrants.`,
  };
};

export default AreaToolbarZoomManager;
